import { z } from 'zod';

/*
 * Retail Media: einfacher Einbau in den Shop (Migration 0660).
 *
 * Shopify: nur über die App „DXL Retail Media“ (Theme App Extension, eigenes Repo `dxl-retail-media`) – sie bindet
 * die Werbeflächen über den Theme-Editor ein und setzt den Werbeschlüssel selbst; keine ScriptTags. Die
 * Auto-Platzierung setzt die Flächen an bekannten Stellen der Online-Store-2.0-Themes (Dawn & Co.). Andere Shops:
 * eine Skript-Zeile, die Flächen kommen aus Platzierungsregeln, die der KI-Assistent vorschlägt. Das SDK liest die
 * Regeln über GET /v1/rm/einbau.
 */

/** Installationslink der App „DXL Retail Media“ im Shopify App Store – wird nach der Freigabe im App Store gesetzt (leer = kein Knopf) */
export const RM_APP_INSTALL_URL: string = '';

export const SHOPSYSTEME = { shopify: 'Shopify', anderer: 'Anderer Shop' } as const;
export type Shopsystem = '' | keyof typeof SHOPSYSTEME;

export const EINBAU_SEITEN = {
  start: 'Startseite',
  kategorie: 'Kategorieseite',
  suche: 'Suchergebnisse',
  produkt: 'Produktseite',
  warenkorb: 'Warenkorb',
} as const;
export type EinbauSeite = keyof typeof EINBAU_SEITEN;

export const EINBAU_POSITIONEN = {
  davor: 'Davor',
  danach: 'Danach',
  anfang: 'Innen am Anfang',
  ende: 'Innen am Ende',
  /** im Produktraster als eigene Kachel nach Produkt Nr. „nach“ */
  raster: 'Im Produktraster',
  /** bestehender Banner: zeigt die Anzeige, ohne Anzeige bleibt der Banner */
  ersetzen: 'Banner ersetzen',
} as const;
export type EinbauPosition = keyof typeof EINBAU_POSITIONEN;

/** Platzierungen, die auf einem Seitentyp sinnvoll sind – die erste ist der Vorschlag */
export const EINBAU_PLATZIERUNGEN: Record<EinbauSeite, readonly string[]> = {
  start: ['start_hero', 'start_takeover', 'start_kacheln', 'leaderboard'],
  kategorie: ['kategorie_grid', 'kategorie_banner', 'grid_kachel', 'leaderboard'],
  suche: ['suche_treffer', 'suche_banner', 'grid_kachel', 'leaderboard'],
  produkt: ['produkt_detail', 'leaderboard'],
  warenkorb: ['warenkorb'],
};

/** Banner-Platzierungen: das SDK rendert den Banner selbst (Container data-dxl-rm) – die übrigen sind Produktkarten */
export const BANNER_PLATZIERUNGEN: readonly string[] = [
  'start_hero', 'start_takeover', 'start_kacheln', 'kategorie_banner', 'suche_banner', 'markenseite', 'leaderboard', 'grid_kachel', 'checkout_bestaetigung',
];

/** Seiten des Code-Baukastens (mehr als die Seitentypen der Regeln) */
export const BAUKASTEN_SEITEN = {
  start: 'Startseite',
  kategorie: 'Kategorie / Collection',
  suche: 'Suchergebnisse',
  produkt: 'Produktseite',
  warenkorb: 'Warenkorb',
  bestellung: 'Bestellbestätigung',
  marke: 'Markenseite',
  alle: 'Jede Seite',
} as const;
export type BaukastenSeite = keyof typeof BAUKASTEN_SEITEN;

/** Auf welchen Seiten eine Platzierung passt – eigene WKZ-Flächen des Marktplatzes passen überall */
export const PLATZIERUNG_SEITEN: Record<string, readonly BaukastenSeite[]> = {
  kategorie_grid: ['kategorie'], suche_treffer: ['suche'], produkt_detail: ['produkt'], warenkorb: ['warenkorb'],
  suche_banner: ['suche'], kategorie_banner: ['kategorie'], grid_kachel: ['kategorie', 'suche'], markenseite: ['marke'],
  leaderboard: ['alle', 'start', 'kategorie', 'suche', 'produkt'], checkout_bestaetigung: ['bestellung'],
  start_hero: ['start'], start_takeover: ['start'], start_kacheln: ['start'],
};

/** Formate des Baukastens: Kampagnentypen plus WKZ (Festpreis-Flächen) */
export const BAUKASTEN_FORMATE = {
  sponsored_product: 'Sponsored Products (Produktkarten)',
  sponsored_brand: 'Sponsored Brand (Markenbanner)',
  display: 'Display (Banner)',
  wkz: 'WKZ-Fläche (Hero, Takeover, Kacheln, eigene Flächen)',
} as const;
export type BaukastenFormat = keyof typeof BAUKASTEN_FORMATE;

export const EINBAU_GRENZEN = {
  regeln: 20,
  /** Zeichen je Seite im Auftrag an die KI (vereinfachtes HTML-Gerüst) */
  seiteZeichen: 12_000,
  kiMaxTokens: 1_500,
} as const;

/**
 * Auto-Platzierung für Shopify-Themes (Online Store 2.0) – dieselbe Liste steht in rm-sdk.js (Test prüft den
 * Gleichstand). Selektoren als Liste: der erste Treffer zählt. abschnitt: hinter bzw. vor den ganzen Theme-Abschnitt
 * (.shopify-section) statt in ihn hinein.
 */
export interface AutoRegel {
  seite: EinbauSeite;
  platzierung: string;
  selektor: string;
  position: EinbauPosition;
  abschnitt?: boolean;
  /** nur position raster: nach diesem Produkt */
  nach?: number;
}
const RASTER = '#ProductGridContainer, #product-grid, [id^="product-grid"], .collection .product-grid, .product-grid, .collection-grid, #CollectionProductGrid';
export const SHOPIFY_AUTO_REGELN: readonly AutoRegel[] = [
  { seite: 'start', platzierung: 'start_hero', selektor: 'main#MainContent, #MainContent, main[role="main"], main', position: 'anfang' },
  // Kollektion: Banner über der Liste, darunter Sponsored Products, Display als Kachel nach dem 4. Produkt
  { seite: 'kategorie', platzierung: 'kategorie_banner', selektor: RASTER, position: 'davor' },
  { seite: 'kategorie', platzierung: 'kategorie_grid', selektor: RASTER, position: 'davor' },
  { seite: 'kategorie', platzierung: 'grid_kachel', selektor: '#product-grid, ul.product-grid, .product-grid, .collection-grid', position: 'raster', nach: 4 },
  { seite: 'suche', platzierung: 'suche_banner', selektor: `${RASTER}, .template-search__results`, position: 'davor' },
  { seite: 'suche', platzierung: 'suche_treffer', selektor: `${RASTER}, .template-search__results`, position: 'davor' },
  { seite: 'produkt', platzierung: 'produkt_detail', selektor: '[id^="MainProduct-"], product-info, section.product, .product-section, [data-section-type="product"], .product', position: 'danach', abschnitt: true },
  { seite: 'warenkorb', platzierung: 'warenkorb', selektor: 'cart-items, #main-cart-items, form[action$="/cart"], .cart', position: 'danach', abschnitt: true },
];

/** Einfache CSS-Selektoren: Tag, #id, .klasse, [attribut], Kombinatoren Leerzeichen, > und Komma – kein HTML */
export const EINBAU_SELEKTOR = /^[A-Za-z0-9_\-#.[\]="'^$*~|:() >,+]{1,200}$/;

/** Adresse des Shops: https ergänzen, nur http(s), nur Herkunft (ohne Pfad) – null bei ungültiger Eingabe */
export function shopUrlNormalisieren(eingabe: string): string | null {
  const t = eingabe.trim();
  if (!t) return null;
  try {
    const u = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(t) ? t : `https://${t}`);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    if (!u.hostname.includes('.') || u.username || u.password) return null;
    return u.origin;
  } catch {
    return null;
  }
}

/** Die eine Zeile für den Shop (vor </head> oder am Ende von <body>) */
export const skriptZeile = (basis: string, schluessel: string) =>
  `<script src="${basis.replace(/\/+$/, '')}/rm-sdk.js" data-rm-schluessel="${schluessel}" data-rm-auto async></script>`;

/* ------------------------------------------------------------------ Eingaben */

const regelFelder = {
  seite: z.enum(Object.keys(EINBAU_SEITEN) as [EinbauSeite, ...EinbauSeite[]], { errorMap: () => ({ message: 'Bitte einen Seitentyp wählen' }) }),
  platzierung: z.string().trim().regex(/^[a-z0-9_]{2,40}$/, 'Bitte eine Platzierung wählen'),
  selektor: z.string().trim().min(1, 'Bitte einen CSS-Selektor angeben').max(200, 'Höchstens 200 Zeichen')
    .regex(EINBAU_SELEKTOR, 'Bitte nur einfache CSS-Selektoren verwenden (z. B. #produkte oder .produkt-raster)'),
  position: z.enum(Object.keys(EINBAU_POSITIONEN) as [EinbauPosition, ...EinbauPosition[]], { errorMap: () => ({ message: 'Bitte eine Position wählen' }) }),
  anzahl: z.number().int().min(1, 'Mindestens 1').max(12, 'Höchstens 12').nullable().optional(),
  nach: z.number().int().min(1, 'Mindestens 1').max(48, 'Höchstens 48').nullable().optional(),
};
export const PlatzierungsregelSchema = z.object(regelFelder)
  .refine((r) => r.position !== 'raster' || !!r.nach, { message: 'Bitte angeben, nach welchem Produkt die Kachel erscheint', path: ['nach'] })
  .refine((r) => r.position !== 'ersetzen' || BANNER_PLATZIERUNGEN.includes(r.platzierung), { message: 'Einen Banner kann nur ein Bannerformat ersetzen', path: ['platzierung'] });
export type PlatzierungsregelEingabe = z.infer<typeof PlatzierungsregelSchema>;

export const RegelnSpeichernSchema = z.object({
  regeln: z.array(z.object({ ...regelFelder, quelle: z.enum(['ki', 'manuell']).optional() })
    .refine((r) => r.position !== 'raster' || !!r.nach, { message: 'Bitte angeben, nach welchem Produkt die Kachel erscheint', path: ['nach'] })
    .refine((r) => r.position !== 'ersetzen' || BANNER_PLATZIERUNGEN.includes(r.platzierung), { message: 'Einen Banner kann nur ein Bannerformat ersetzen', path: ['platzierung'] }))
    .max(EINBAU_GRENZEN.regeln, `Höchstens ${EINBAU_GRENZEN.regeln} Regeln`),
});

const shopUrl = z.string().trim().max(300, 'Höchstens 300 Zeichen')
  .refine((s) => !s || shopUrlNormalisieren(s) !== null, 'Bitte eine gültige Adresse angeben, z. B. https://www.ihr-shop.de');

export const EinbauEinstellungSchema = z.object({
  shopsystem: z.enum(['shopify', 'anderer']).optional(),
  shopUrl: shopUrl.optional(),
  autoPlatzierung: z.boolean().optional(),
  /** Produktadresse, wenn der Katalog keinen Link hat: https://…/{handle} usw. */
  produktUrlMuster: z.string().trim().max(500, 'Höchstens 500 Zeichen').optional()
    .refine((s) => !s || (/^https?:\/\/[^\s"'<>]+$/i.test(s) && /\{(handle|sku|ean|id)\}/.test(s)),
      'Bitte eine vollständige Adresse mit einem Platzhalter angeben, z. B. https://www.ihr-shop.de/p/{sku}'),
  /** Produktfeed (Google-Shopping-XML, CSV, Excel) – nur Shops ohne Marktplatz */
  feedUrl: z.string().trim().max(1000, 'Höchstens 1000 Zeichen').optional()
    .refine((s) => !s || /^https?:\/\/[^\s"'<>]+$/i.test(s), 'Bitte die vollständige Adresse des Feeds mit https:// angeben'),
});

const seitenAdresse = z.string().trim().max(500, 'Höchstens 500 Zeichen').optional()
  .refine((s) => !s || /^https?:\/\//i.test(s), 'Bitte die vollständige Adresse mit https:// angeben');

export const EinbauAnalyseSchema = z.object({
  url: z.string().trim().min(1, 'Bitte die Adresse Ihres Shops angeben').pipe(shopUrl),
  /** optional selbst angegebene Seiten, sonst sucht der Assistent */
  kategorie: seitenAdresse,
  produkt: seitenAdresse,
  suche: seitenAdresse,
  bestaetigt: z.literal(true, { errorMap: () => ({ message: 'Bitte bestätigen Sie die Kosten' }) }),
});
export type EinbauAnalyseEingabe = z.infer<typeof EinbauAnalyseSchema>;

export const EinbauPruefenSchema = z.object({ url: shopUrl.optional() });

/* ------------------------------------------------------------------ Antworten */

export interface Platzierungsregel {
  id: string;
  seite: EinbauSeite;
  platzierung: string;
  selektor: string;
  position: EinbauPosition;
  anzahl: number | null;
  nach: number | null;
  quelle: 'ki' | 'manuell';
}

/** Regeln, wie das SDK sie bekommt (GET /v1/rm/einbau) */
export interface EinbauRegelnOeffentlich {
  /** Auto-Platzierung für Shopify-Themes, wo keine eigene Regel greift */
  auto: boolean;
  regeln: { seite: EinbauSeite; platzierung: string; selektor: string; position: EinbauPosition; anzahl?: number; nach?: number }[];
}

export interface EinbauShopify {
  /** Shop mit der App „DXL Marketplace“ verbunden (Marktplatz-Shop) */
  verbunden: boolean;
  /** myshopify-Domain */
  shop: string;
  /** Domain des Onlineshops (für Prüfung und Vorschau) */
  domain: string;
  /** App „DXL Retail Media“ im Shop installiert und aktiv – sie bindet die Werbeflächen ein */
  rmApp: boolean;
  /** myshopify-Domain des Shops mit der App „DXL Retail Media“ */
  rmAppShop: string;
  rmAppSeit: string | null;
}

export interface WerbungEinbauStand {
  shopsystem: Shopsystem;
  basisUrl: string;
  /** Schlüssel der Einbindung (öffentlich) – wert nur, wenn er im Klartext vorliegt */
  schluessel: { id: string; anfang: string; wert: string | null } | null;
  /** fertige Zeile für den Shop – null ohne Schlüssel */
  skriptZeile: string | null;
  shopUrl: string;
  autoPlatzierung: boolean;
  shopify: EinbauShopify;
  regeln: Platzierungsregel[];
  pruefung: EinbauPruefung | null;
  /** Shop nur mit Retail Media (Katalog per Feed möglich) – sonst Marktplatz (Produkte der Händler) */
  standalone: boolean;
  produktUrlMuster: string;
  /** Katalog per Produktfeed (nur standalone) */
  feed: { url: string; am: string | null; laeuft: boolean; text: string; fehler: string };
  /** aktive Flächen im Shop (ohne Buy Box, Offsite, sonstige WKZ-Leistungen) mit ihren Formaten */
  platzierungen: EinbauFlaeche[];
}

export interface EinbauFlaeche {
  schluessel: string;
  name: string;
  /** zugelassene Kampagnenformate (ohne ausgeblendete wie Offsite) */
  formate: string[];
  /** per WKZ buchbar */
  wkz: boolean;
  /** Vorgaben für das Werbemittel (WKZ) */
  masse: string;
}

/** Gefundener Banner im Shop (Banner-Finder) */
export interface EinbauBanner {
  seite: EinbauSeite;
  url: string;
  selektor: string;
  /** Vorschaubild (absolute Adresse) */
  bild: string;
  text: string;
  /** Vorschlag für die Platzierung */
  platzierung: string;
}

export interface EinbauKosten {
  /** Höchstwert für eine Analyse */
  tokens: number;
  cent: number;
  /** KI von DXL (wird abgerechnet) – sonst eigener Schlüssel */
  dxl: boolean;
  /** KI eingerichtet */
  bereit: boolean;
  hinweis: string;
}

export interface EinbauVorschlag {
  seite: EinbauSeite;
  platzierung: string;
  selektor: string;
  position: EinbauPosition;
  begruendung: string;
  /** Treffer des Selektors im geladenen HTML (ungefähr – ohne JavaScript des Shops) */
  treffer: number;
  nach?: number | null;
}

export interface EinbauSeiteGeladen {
  seite: EinbauSeite;
  url: string;
  ok: boolean;
  fehler: string;
}

export interface EinbauAnalyse {
  shopify: boolean;
  seiten: EinbauSeiteGeladen[];
  vorschlaege: EinbauVorschlag[];
  kosten: { tokens: number; cent: number } | null;
}

export type EinbauTon = 'gut' | 'warnung' | 'fehler';

export interface EinbauPruefPunkt {
  ton: EinbauTon;
  titel: string;
  text: string;
}

export interface EinbauPruefung {
  url: string;
  zeit: string;
  /** SDK gefunden und Schlüssel gültig */
  ok: boolean;
  punkte: EinbauPruefPunkt[];
}

/* ------------------------------------------------------------------ Bestellungen ohne Bestellimport */

/**
 * POST /v1/rm/bestellung – vom Shop-SDK auf der Bestellbestätigung (öffentlicher Schlüssel) oder vom Server des Shops
 * (geheimer Schlüssel). produkt: ID, Artikelnummer, EAN oder Handle wie im Katalog. Beträge in Cent.
 */
export const BestellungMeldungSchema = z.object({
  schluessel: z.string().max(120).optional(),
  bestellung: z.string().trim().min(1).max(120),
  sitzung: z.string().trim().min(8).max(128),
  summe_cent: z.number().nonnegative().max(10_000_000_000).optional(),
  waehrung: z.string().trim().max(3).optional(),
  positionen: z.array(z.object({
    produkt: z.string().trim().min(1).max(200),
    menge: z.number().positive().max(100_000).optional(),
    preis_cent: z.number().nonnegative().max(1_000_000_000).optional(),
  })).max(200).default([]),
});
export type BestellungMeldung = z.infer<typeof BestellungMeldungSchema>;

/** Muster der Produktadresse: absolute Adresse mit mindestens einem Platzhalter */
export const PRODUKT_URL_PLATZHALTER = ['{handle}', '{sku}', '{ean}', '{id}'] as const;

/* ------------------------------------------------------------------ Code-Baukasten */

export interface BaukastenWahl {
  platzierung: string;
  /** Anzahl Produktkarten bzw. Kacheln (leer = Standard der Platzierung) */
  anzahl?: number | null;
  /** Überschrift über Produktkarten */
  titel?: string;
  /** Position der Kennzeichnung (ANZEIGE_LABEL) */
  label?: 'links' | 'rechts';
  /** bestehenden Banner zur Werbefläche machen statt neue Fläche */
  ersetzen?: boolean;
}

const attrWert = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Genau ein kopierfertiger Schnipsel – den Kontext (Kategorie, Suche, Produkt) ermittelt das Skript aus der Seite */
export function baukastenSchnipsel(w: BaukastenWahl): string {
  const pl = /^[a-z0-9_]{2,40}$/.test(w.platzierung) ? w.platzierung : 'kategorie_banner';
  const label = w.label === 'rechts' ? ' data-rm-label="rechts"' : '';
  if (w.ersetzen) {
    return `<!-- das Attribut an Ihren bestehenden Banner anhängen -->\n<div class="ihr-banner" data-dxl-rm-ersetzen="${pl}"${label}>\n  <!-- Ihr Banner bleibt sichtbar, solange keine Anzeige läuft -->\n</div>`;
  }
  const banner = BANNER_PLATZIERUNGEN.includes(pl);
  const teile = [banner ? `data-dxl-rm="${pl}"` : `data-rm-platzierung="${pl}"`];
  if (w.anzahl && w.anzahl >= 1 && w.anzahl <= 12) teile.push(`data-rm-anzahl="${Math.round(w.anzahl)}"`);
  if (!banner && w.titel?.trim()) teile.push(`data-rm-titel="${attrWert(w.titel.trim().slice(0, 80))}"`);
  return `<div ${teile.join(' ')}${label}></div>`;
}
